import React from "react";
import { RefreshCw, ChevronDown, Globe, Calendar, Info, FileSpreadsheet } from "lucide-react";
import { fmtDate } from "../ExcelUploadManager";

const VIEWS = [["inbound","Inbound"],["outbound","Outbound"],["all","All"],["platform","Platform"],["pallets","Pallets"]];

// Title, "data as of" line, view switch and the toolbar buttons
export default function DashboardHeader({
    lastRefresh, hasExcel, excelReports, storage, activeView, loading, tenantOnly, otherOutbound = [],
    onSelectView, onUpload, onIncoterms, onRefresh,
}) {
    return (
        <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
                <h1 className="text-2xl font-bold text-white">{tenantOnly ? "Control Tower" : "Good morning, Admin 👋"}</h1>
                <p className="text-sm text-slate-400 mt-0.5">
                    Real-time view of your global supply chain.
                    {lastRefresh && (
                        <span className="ml-2 text-slate-600 text-[11px]">
                            Updated {lastRefresh.toLocaleTimeString()}
                        </span>
                    )}
                </p>
                <p className="text-[11px] text-slate-500 mt-1 flex items-center gap-1.5">
                    <FileSpreadsheet size={11} className={hasExcel ? "text-green-400" : "text-slate-600"}/>
                    {hasExcel ? (
                        ["inbound", "outbound"].map(t => (
                            <span key={t} className={activeView === t || activeView === "all" ? "underline decoration-green-500/60 underline-offset-4" : "opacity-60"}>
                                {t === "inbound" ? "Inbound" : "Outbound"}:{" "}
                                {excelReports[t]
                                    ? <span className="text-slate-300">report {fmtDate(excelReports[t].reportDate)}</span>
                                    : <span className="text-slate-600">no upload yet</span>}
                                {t === "inbound" && <span className="text-slate-700 mx-1.5">·</span>}
                            </span>
                        ))
                    ) : otherOutbound.length ? (
                        <span>Outbound: {otherOutbound.map(r => <span key={r.label} className="text-slate-300">{r.label} {fmtDate(r.reportDate)}</span>).reduce((a, b) => [a, " · ", b])}</span>
                    ) : tenantOnly ? "No Excel reports imported yet — upload your inbound or outbound report to get started."
                      : "No Excel reports imported yet — shipment figures below come from platform records."}
                    {storage && !storage.persistent && (
                        <span className="ml-1.5 px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400 text-[10px] font-semibold"
                            title="Imported reports are held in the backend's memory and disappear when it restarts">
                            Dev mode · in-memory
                        </span>
                    )}
                </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
                <div className="flex bg-[#111827] border border-[#1F2937] rounded-lg p-0.5" title="Choose which data the cards, map and table show">
                    {VIEWS.filter(([id]) => !tenantOnly || id !== "platform").map(([id, label]) => (
                        <button key={id} onClick={() => onSelectView(id)}
                            title={id === "all" ? "Inbound + outbound reports combined" : undefined}
                            className={`px-3 py-1.5 rounded-md text-sm transition-colors ${activeView === id ? "bg-[#1F2937] text-white" : "text-slate-500 hover:text-slate-300"}`}>
                            {label}
                            {id === "pallets" && <span className="ml-1.5 px-1 py-px rounded bg-amber-400 text-[9px] font-bold text-black align-middle">NEW</span>}
                        </button>
                    ))}
                </div>
                <button
                    onClick={onUpload}
                    className="flex items-center gap-2 px-3 py-2 bg-[#111827] border border-green-500/40 rounded-lg text-sm text-green-400 hover:border-green-400 hover:bg-green-500/10 transition-colors"
                >
                    <FileSpreadsheet size={14}/> Upload Reports
                </button>
                <button className="flex items-center gap-2 px-3 py-2 bg-[#111827] border border-[#1F2937] rounded-lg text-sm text-slate-300 hover:border-slate-500 transition-colors">
                    <Globe size={14} className="text-slate-400"/> All Entities <ChevronDown size={13}/>
                </button>
                <button className="flex items-center gap-2 px-3 py-2 bg-[#111827] border border-[#1F2937] rounded-lg text-sm text-slate-300 hover:border-slate-500 transition-colors">
                    <Calendar size={14} className="text-slate-400"/> Last 30 Days <ChevronDown size={13}/>
                </button>
                <button
                    onClick={onIncoterms}
                    className="flex items-center gap-2 px-3 py-2 bg-[#111827] border border-blue-500/40 rounded-lg text-sm text-blue-400 hover:border-blue-400 hover:bg-blue-500/10 transition-colors"
                    title="Incoterms 2020 Reference Guide"
                >
                    <Info size={14}/> Incoterms
                </button>
                <button
                    onClick={onRefresh}
                    disabled={loading}
                    className="flex items-center gap-2 px-3 py-2 bg-[#111827] border border-[#1F2937] rounded-lg text-sm text-slate-300 hover:border-slate-500 transition-colors disabled:opacity-50"
                >
                    <RefreshCw size={14} className={loading ? "animate-spin" : ""}/>
                    Refresh
                </button>
            </div>
        </div>
    );
}
